import useFetch from "v4/hooks/useFetch";
import {generateUrl} from "v4/services/api.service";
import {LOGIN_2FA} from "v4/data/apiRoutes";
import {useEffect} from "react";
import {useAuthContext} from "v4/contexts/AuthContext";
import {useTranslation} from "react-i18next";
import jsonToFormData from "v4/utils/jsonToFormData";
import {CHECKBOX_TYPE, TEXT_TYPE} from "v4/data/fieldTypes";
import {removeLSItem} from "v4/services/localStorage.service";
import {routesBase} from "v4/data/appRoutes";
import {useHistory} from "react-router-dom";
import useNotification from "v4/hooks/useNotification";
import {get2FAValidity} from "v4/pages/front/Login/hooks/useLogin";
import UserService from "api/user/user";

export default function useLogin2FA() {
    const {t} = useTranslation();
    const {setToken} = useAuthContext();
    const [{data, errorData, isLoading}, login2FAFetch] = useFetch();
    const {replace, location} = useHistory();
    const {addInfoNotification} = useNotification();

    const onSubmit = (data) => {
        const dataToSend = jsonToFormData(data);
        if ('false' === dataToSend.get('two_factor_login[trustedDevice]')) {
            dataToSend.delete('two_factor_login[trustedDevice]');
        }

        login2FAFetch({
            url: process.env.REACT_APP_POL_CORE_URL + generateUrl(LOGIN_2FA, {}, false),
            config: {
                method: 'POST',
                body: dataToSend,
                credentials: 'include', // Include cookies for session management
                notoken: true,
            }
        });
    }

    useEffect(() => {
        const {isValid, remainingTime} = get2FAValidity();

        if (!isValid) {
            replace({
                pathname: routesBase.baseLogin,
                search: location.search,
            });
            return;
        }

        // Redirect to login page after remaining time
        const timeoutId = setTimeout(() => {
            removeLSItem('login2FALastTry');
            addInfoNotification(t('login2fa_timeout_notification'));
            replace({
                pathname: routesBase.baseLogin,
                search: location.search,
            });
        }, remainingTime > 0 ? remainingTime : 0);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!data?.data?.auth_token) return;

        removeLSItem('login2FALastTry');

        if (data.data.redirect_url) {
            UserService.setRedirectTo(data.data.redirect_url);
        }

        setToken(data.data.auth_token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const form = {
        type: "success:get-form-schema",
        data: {
            "two_factor_login[code]": {
                attr: {
                    autocomplete: "one-time-code",
                    inputmode: "numeric",
                    pattern: "[\\d]{6}"
                },
                constraints: {
                    required: true,
                    readonly: false
                },
                label: t('verification_code'),
                name: "code",
                type: TEXT_TYPE,
            },
            "two_factor_login[trustedDevice]": {
                attr: [],
                constraints: {
                    required: false,
                    readonly: false
                },
                name: "trustedDevice",
                label: t('trust_device'),
                type: CHECKBOX_TYPE,
            }
        }
    }

    const onGoBackToLogin = () => removeLSItem('login2FALastTry');

    return {
        form,
        formErrorMessage: errorData?.details,
        onSubmit,
        isLoading,
        onGoBackToLogin,
    }
}
